import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Headphones, Play, Pause, SkipForward, SkipBack, Volume2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mapeo Cap → título corto (para mostrar en el player)
const CAP_TITLES = {
  1: "La Nación que aprendió a sobrevivir",
  2: "El fracaso patrimonial argentino",
  3: "La escasez como diseño institucional",
  4: "El agotamiento de las doctrinas del siglo XX",
  5: "¿Qué es el Capitalismo de Abundancia?",
  6: "Propiedad, patrimonio y libertad real",
  7: "La Nación como plataforma de abundancia",
  8: "Soberanía operativa",
  9: "La moneda como decisión política",
  10: "El Capital Ciudadano como Patrimonio Base",
  11: "BVA y PVA",
  12: "El nuevo pacto fiscal",
  13: "El segundo ciclo del sistema",
  14: "FDRE y renta intergeneracional",
  15: "Sin orden no hay abundancia",
  16: "Justicia rápida, corrupción imposible",
  17: "El Estado fuerte, limitado y eficaz",
  18: "Los recursos estratégicos",
  19: "Energía soberana",
  20: "Infraestructura y logística",
  21: "Revolución Pesquera",
  22: "Proteína Pesquera",
  23: "Trabajo, empresa nacional, industrialización",
  24: "Turismo interno",
  25: "Minería del siglo XXI",
  26: "Complejo proteico-alimentario",
  27: "Tecnología, defensa y autonomía estratégica",
  28: "Argentina en el nuevo orden mundial",
  29: "La doctrina argentina",
  30: "Integración soberana con bloques económicos",
};

export const AudiobookPlayer = () => {
  const [tracks, setTracks] = useState([]);
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState({ current: 0, duration: 0 });
  const [settings, setSettings] = useState({ voice: "onyx", model: "tts-1", speed: 1.0 });
  const audioRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/book/audio`).then(({ data }) => {
      setTracks(data.sort((a, b) => a.capitulo - b.capitulo));
    }).catch(() => {});
    axios.get(`${API}/book/audio/settings`).then(({ data }) => {
      if (data) setSettings({
        voice: data.voice || "onyx",
        model: data.model || "tts-1",
        speed: typeof data.speed === "number" ? data.speed : 1.0,
      });
    }).catch(() => {});
  }, []);

  // Aplica velocidad configurada por el admin al elemento <audio>
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = settings.speed || 1.0;
    }
  }, [settings.speed, idx, tracks.length]);

  useEffect(() => {
    if (!audioRef.current) return;
    audioRef.current.pause();
    setPlaying(false);
    setProgress({ current: 0, duration: 0 });
  }, [idx]);

  const togglePlay = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      a.play().then(() => setPlaying(true)).catch(() => {});
    } else {
      a.pause();
      setPlaying(false);
    }
  };

  const next = () => idx < tracks.length - 1 && setIdx(idx + 1);
  const prev = () => idx > 0 && setIdx(idx - 1);

  const fmt = (s) => {
    if (!isFinite(s)) return "0:00";
    const m = Math.floor(s / 60);
    const ss = Math.floor(s % 60).toString().padStart(2, "0");
    return `${m}:${ss}`;
  };

  if (tracks.length === 0) return null;

  const current = tracks[idx];
  const capNum = current.capitulo;
  const capTitle = CAP_TITLES[capNum] || `Capítulo ${capNum}`;
  const pct = progress.duration ? (progress.current / progress.duration) * 100 : 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.6 }}
      className="mt-12 border border-[#F5B400]/40 bg-gradient-to-br from-[#0A192F] to-[#0A2463] text-white p-6 sm:p-8 shadow-xl"
      data-testid="audiobook-player"
    >
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 bg-[#F5B400]/20 border border-[#F5B400]/40 flex items-center justify-center">
          <Headphones className="text-[#F5B400]" size={20} />
        </div>
        <div>
          <div className="font-headings uppercase tracking-wide text-xs text-[#F5B400]">
            Audiolibro
          </div>
          <h3 className="font-headings uppercase tracking-tight text-xl text-white leading-tight">
            Escuchá el libro
          </h3>
        </div>
      </div>

      {/* Track actual */}
      <div className="bg-white/[0.05] border border-white/10 p-4 mb-4">
        <div className="flex items-center gap-3 mb-3">
          <span className="text-3xl font-headings font-bold text-[#F5B400] tabular-nums">
            {String(capNum).padStart(2, "0")}
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/50 font-headings">
              Capítulo {capNum}
            </div>
            <div className="font-headings uppercase text-sm sm:text-base text-white truncate">
              {capTitle}
            </div>
          </div>
        </div>

        {/* Progress bar */}
        <div
          className="h-1.5 bg-white/10 mb-2 cursor-pointer relative"
          onClick={(e) => {
            const a = audioRef.current;
            if (!a || !a.duration) return;
            const rect = e.currentTarget.getBoundingClientRect();
            const p = (e.clientX - rect.left) / rect.width;
            a.currentTime = p * a.duration;
          }}
        >
          <div className="absolute inset-y-0 left-0 bg-[#F5B400]" style={{ width: `${pct}%` }} />
        </div>
        <div className="flex justify-between text-[10px] text-white/40 font-headings tabular-nums">
          <span>{fmt(progress.current)}</span>
          <span>{fmt(progress.duration)}</span>
        </div>

        <audio
          ref={audioRef}
          src={current.file}
          preload="metadata"
          onTimeUpdate={(e) => setProgress({ current: e.target.currentTime, duration: e.target.duration || 0 })}
          onLoadedMetadata={(e) => setProgress((p) => ({ ...p, duration: e.target.duration || 0 }))}
          onEnded={() => idx < tracks.length - 1 && setIdx(idx + 1)}
        />

        {/* Controls */}
        <div className="flex items-center justify-center gap-2 mt-4">
          <button
            onClick={prev}
            disabled={idx === 0}
            className="p-2 text-white/70 hover:text-[#F5B400] disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid="audio-prev"
          >
            <SkipBack size={20} />
          </button>
          <button
            onClick={togglePlay}
            className="w-12 h-12 rounded-full bg-[#F5B400] hover:bg-[#d99f00] text-[#0A192F] inline-flex items-center justify-center"
            data-testid="audio-play"
          >
            {playing ? <Pause size={20} /> : <Play size={20} className="ml-0.5" />}
          </button>
          <button
            onClick={next}
            disabled={idx >= tracks.length - 1}
            className="p-2 text-white/70 hover:text-[#F5B400] disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid="audio-next"
          >
            <SkipForward size={20} />
          </button>
        </div>
      </div>

      {/* Selector de capítulo */}
      <div className="mb-3" data-testid="audio-chapter-select-wrap">
        <label className="block text-[10px] uppercase tracking-[0.25em] text-white/50 font-headings mb-2">
          Elegir capítulo
        </label>
        <select
          value={idx}
          onChange={(e) => setIdx(Number(e.target.value))}
          className="w-full bg-white/[0.06] border border-white/15 hover:border-[#F5B400]/60 focus:border-[#F5B400] focus:outline-none text-white text-sm font-headings uppercase tracking-wide px-4 py-3 appearance-none cursor-pointer transition-colors"
          data-testid="audio-chapter-select"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23F5B400' stroke-width='2.5'><polyline points='6 9 12 15 18 9'/></svg>\")",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "right 16px center",
            paddingRight: "44px",
          }}
        >
          {tracks.map((t, i) => (
            <option key={t.capitulo} value={i} className="bg-[#0A192F]">
              {`Cap. ${String(t.capitulo).padStart(2, "0")} · ${CAP_TITLES[t.capitulo] || "Capítulo " + t.capitulo}`}
            </option>
          ))}
        </select>
      </div>

      {/* Lista compacta de capítulos */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-1.5 max-h-48 overflow-y-auto pr-1" data-testid="audio-chapter-grid">
        {tracks.map((t, i) => (
          <button
            key={t.capitulo}
            onClick={() => setIdx(i)}
            className={`text-left text-[11px] uppercase tracking-wide font-headings px-2 py-1.5 transition-colors ${
              i === idx
                ? "bg-[#F5B400] text-[#0A192F]"
                : "bg-white/[0.04] text-white/70 hover:bg-white/10 hover:text-white"
            }`}
            data-testid={`audio-chapter-${t.capitulo}`}
          >
            <span className="tabular-nums">{String(t.capitulo).padStart(2, "0")}</span>
            <span className="ml-2 truncate">{(CAP_TITLES[t.capitulo] || "").split(/[.,:]/)[0]}</span>
          </button>
        ))}
      </div>

      <p className="text-[10px] text-white/40 mt-3 uppercase tracking-[0.2em] font-headings">
        <Volume2 size={11} className="inline mr-1" />
        Tracks disponibles: {tracks.length} de 30
      </p>
    </motion.div>
  );
};

export default AudiobookPlayer;
